import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";
import { API, ASSETS } from "@/data/site";

export default function Footer() {
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSending(true);
    try {
      await axios.post(`${API}/newsletter`, { email: email.trim() });
      toast.success("You're on the list. Warm words, once a month.");
      setEmail("");
    } catch {
      toast.error("Something went wrong — please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <footer className="bg-espresso text-ivory" data-testid="site-footer">
      <div className="mx-auto max-w-7xl px-6 py-24 lg:px-10">
        <div className="grid grid-cols-1 gap-16 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <p className="text-xs font-medium uppercase tracking-[0.25em] text-mauve">
              The inbornbirth letter
            </p>
            <h3 className="mt-4 font-serif text-4xl font-light leading-tight lg:text-5xl">
              Warm words for the <span className="italic text-mauve">fourth trimester</span>,
              once a month.
            </h3>
            <form onSubmit={subscribe} className="mt-8 flex max-w-md items-center gap-3" data-testid="newsletter-form">
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Your email address"
                data-testid="newsletter-email-input"
                className="w-full rounded-full border border-ivory/20 bg-transparent px-5 py-3 text-sm text-ivory placeholder:text-ivory/40 focus:border-mauve"
              />
              <button
                type="submit"
                disabled={sending}
                data-testid="newsletter-submit-button"
                className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-sienna text-ivory transition-colors hover:bg-sienna-dark disabled:opacity-50"
                aria-label="Subscribe"
              >
                <ArrowRight className="h-4 w-4" strokeWidth={1.5} />
              </button>
            </form>
          </div>

          <div className="lg:col-span-3 lg:col-start-7">
            <p className="text-xs font-medium uppercase tracking-[0.25em] text-mauve">Explore</p>
            <div className="mt-5 flex flex-col gap-3 text-sm text-ivory/70">
              <Link to="/book" data-testid="footer-link-book" className="transition-colors hover:text-ivory">Book a visit</Link>
              <Link to="/journal" data-testid="footer-link-journal" className="transition-colors hover:text-ivory">Journal</Link>
              <Link to="/appointments" data-testid="footer-link-appointments" className="transition-colors hover:text-ivory">Appointments</Link>
              <a href="mailto:athena@inbornbirth.com" data-testid="footer-link-email" className="transition-colors hover:text-ivory">athena@inbornbirth.com</a>
              <a href="https://www.westonprice.org" target="_blank" rel="noopener noreferrer" data-testid="footer-link-westonprice" className="transition-colors hover:text-ivory">
                Traditional nourishment reading · westonprice.org
              </a>
              <a href="mailto:care@inbornbirth.com" data-testid="footer-link-care-email" className="transition-colors hover:text-ivory">care@inbornbirth.com <span className="text-ivory/40">(website questions)</span></a>
            </div>
          </div>

          <div className="lg:col-span-3 lg:col-start-10">
            <p className="text-xs font-medium uppercase tracking-[0.25em] text-mauve">Traditional · Womb · Postpartum</p>
            <p className="mt-5 text-sm leading-relaxed text-ivory/70">
              Our signature gathering is Planning for the Fourth Trimester — and beyond
              it, the traditional care carried into your home: companion visits, warm meals,
              belly binding and the rituals of our grandmothers.
            </p>
            <div className="mt-6 h-20 w-20 overflow-hidden rounded-full ring-1 ring-ivory/20">
              <img src={ASSETS.logo} alt="inbornbirth" className="h-full w-full object-cover" data-testid="footer-logo" />
            </div>
          </div>
        </div>

        <div className="mt-20 flex flex-col items-start justify-between gap-4 border-t border-ivory/10 pt-8 text-xs uppercase tracking-[0.2em] text-ivory/40 sm:flex-row sm:items-center">
          <span>© 2026 inbornbirth</span>
          <span>Mother the mother</span>
        </div>
        <p className="mt-6 max-w-2xl text-xs normal-case leading-relaxed tracking-normal text-ivory/40" data-testid="footer-disclaimer">
          Services may be added or retired at any time. Traditional practices such as
          vaginal steaming, womb lifting and belly binding are offered for comfort and
          wellbeing — please consult your physician or midwife before booking.
        </p>
      </div>
    </footer>
  );
}
